import React from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  Check,
  MessageCircle,
  FileText,
  Bell,
  Eye,
  AlertCircle,
  Clock,
  ChevronRight,
  Megaphone,
  Trash2
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { useNotifications } from '../hooks/useNotifications';
import { motion } from 'framer-motion';
import type { Notification } from '../types/notification';

interface NotificationDropdownProps {
  onClose: () => void;
}

export function NotificationDropdown({ onClose }: NotificationDropdownProps) {
  const { notifications, unreadCount, markAsRead, markAllAsRead, deleteAllNotifications } = useNotifications();
  const navigate = useNavigate();

  const displayedNotifications = notifications.slice(0, 3);

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'document_added':
        return <FileText className="h-3.5 w-3.5 text-emerald-600" />;
      case 'new_message':
        return <MessageCircle className="h-3.5 w-3.5 text-primary-600" />;
      case 'post_approved':
        return <Check className="h-3.5 w-3.5 text-emerald-600" />;
      case 'post_rejected':
        return <AlertCircle className="h-3.5 w-3.5 text-red-500" />;
      case 'new_post':
        return <Eye className="h-3.5 w-3.5 text-sky-600" />;
      case 'council_announcement':
        return <Megaphone className="h-3.5 w-3.5 text-amber-600" />;
      default:
        return <Bell className="h-3.5 w-3.5 text-slate-400" />;
    }
  };

  const getIconBg = (type: string) => {
    switch (type) {
      case 'document_added': return 'bg-emerald-50';
      case 'new_message': return 'bg-primary-50';
      case 'post_approved': return 'bg-emerald-50';
      case 'post_rejected': return 'bg-red-50';
      case 'new_post': return 'bg-sky-50';
      case 'council_announcement': return 'bg-amber-50';
      default: return 'bg-slate-100';
    }
  };

  const getTimeAgo = (date: Date) => {
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) return 'A l\'instant';
    if (diffInMinutes < 60) return `${diffInMinutes}m`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h`;
    return format(date, 'dd/MM', { locale: fr });
  };

  const handleNotificationClick = async (notification: Notification) => {
    if (!notification.read) {
      await markAsRead(notification.id);
    }

    onClose();

    switch (notification.type) {
      case 'document_added':
        navigate(`/documents?id=${notification.data?.documentId}`);
        break;
      case 'post_approved':
      case 'post_rejected':
      case 'new_post':
        navigate(`/forum?post=${notification.data?.postId}`);
        break;
      case 'new_message':
        if (notification.data?.conversationId) {
          navigate(`/messages?id=${notification.data.conversationId}`);
        }
        break;
      case 'council_announcement':
        navigate('/council');
        break;
      default:
        break;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -8, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -8, scale: 0.96 }}
      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      className="absolute right-0 mt-2 w-[calc(100vw-2rem)] max-w-sm md:w-96 bg-white rounded-2xl shadow-card border border-slate-200/60 overflow-hidden z-50"
    >
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded-lg bg-primary-50">
            <Bell className="h-3.5 w-3.5 text-primary-600" />
          </div>
          <h3 className="text-xs font-bold text-slate-800">
            Notifications {unreadCount > 0 && (
              <span className="ml-1 px-1.5 py-0.5 rounded-md bg-primary-100 text-primary-700 text-[10px] font-semibold">
                {unreadCount}
              </span>
            )}
          </h3>
        </div>
        {unreadCount > 0 && (
          <button
            onClick={markAllAsRead}
            className="text-[10px] font-medium text-primary-600 hover:text-primary-700 transition-colors hidden md:block"
          >
            Tout marquer comme lu
          </button>
        )}
      </div>

      {notifications.length === 0 && (
        <div className="px-4 py-10 text-center">
          <div className="w-12 h-12 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-3">
            <Bell className="h-6 w-6 text-slate-300" />
          </div>
          <p className="text-xs text-slate-400 font-medium">Aucune notification</p>
        </div>
      )}

      <div className="max-h-[60vh] md:max-h-[calc(100vh-200px)] overflow-y-auto">
        {displayedNotifications.map((notification) => (
          <button
            key={notification.id}
            onClick={() => handleNotificationClick(notification)}
            className={clsx(
              "w-full px-4 py-3 flex items-start hover:bg-slate-50 transition-all border-b border-slate-100/80 last:border-0",
              !notification.read && "bg-primary-50/30"
            )}
          >
            <div className={clsx(
              "flex-shrink-0 w-8 h-8 rounded-xl flex items-center justify-center",
              getIconBg(notification.type)
            )}>
              {getNotificationIcon(notification.type)}
            </div>

            <div className="ml-3 flex-1 text-left min-w-0">
              <div className="flex justify-between items-start gap-1.5">
                <p className={clsx(
                  "text-xs line-clamp-1 flex-1",
                  !notification.read ? "font-semibold text-slate-800" : "font-medium text-slate-600"
                )}>
                  {notification.title}
                </p>
                <div className="flex items-center flex-shrink-0">
                  <Clock className="h-2.5 w-2.5 text-slate-300 mr-0.5" />
                  <span className="text-[10px] text-slate-400">
                    {getTimeAgo(notification.createdAt)}
                  </span>
                </div>
              </div>
              <p className="mt-0.5 text-[11px] text-slate-500 line-clamp-2">
                {notification.message}
              </p>
              <div className="mt-1 flex items-center text-[10px] font-medium text-primary-600">
                <span>Voir</span>
                <ChevronRight className="h-2.5 w-2.5 ml-0.5" />
              </div>
            </div>
          </button>
        ))}
      </div>

      {notifications.length > 0 && (
        <div className="border-t border-slate-100 p-2">
          <button
            onClick={() => {
              if (window.confirm('Supprimer toutes les notifications ?')) {
                deleteAllNotifications();
              }
            }}
            className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-red-600 hover:bg-red-50 rounded-xl transition-all"
          >
            <Trash2 className="h-3 w-3" />
            Tout supprimer
          </button>
        </div>
      )}
    </motion.div>
  );
}
